import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { GridSortItem } from "@mui/x-data-grid";
import { useRef } from "react";
import {
  GetPatientAllPaymentPaymentModeEnum,
  GetPatientAllPaymentTypeOfPaymentEnum,
  GetPatientAllPaymentV2PaymentCategoryEnum,
  PatientPaymentControllerApi,
} from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";

/**
 *
 * @author Atish Bagate
 */
interface Props {
  pagination: DGT_Pagination;
  patientUuid?: string;
  gridSorting: GridSortItem[];
  paymentMethod?: GetPatientAllPaymentPaymentModeEnum;
  paymentCategory?: GetPatientAllPaymentV2PaymentCategoryEnum;
  typeOfPayment?: GetPatientAllPaymentTypeOfPaymentEnum;
  transactionDate?: string;
  transactionId?: string;
  searchString?: string;
  visitId?: string;
  superbillId?: string;
  claimId?: string;
  status?: string;
  /**
   * Archive filter, applied by the backend. `false` returns active (non-archived)
   * cards, `true` returns archived ones. Sent as a query param until it is part
   * of the generated API signature.
   */
  archive?: boolean;
  /** When true, returns only rows where Unused Payment > 0 (Credit Balance tag). */
  creditBalance?: boolean;
  /** When true, returns only rows where Remaining Balance > 0 (Outstanding Balance tag). */
  outstandingBalance?: boolean;
  enabled?: boolean;
}

export function useGetPatientPaymentV2({
  pagination,
  patientUuid,
  gridSorting,
  paymentMethod,
  paymentCategory,
  typeOfPayment,
  transactionDate,
  transactionId,
  searchString,
  visitId,
  superbillId,
  claimId,
  status,
  archive = false,
  creditBalance,
  outstandingBalance,
  enabled,
}: Props): useBaseContent<any> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: "",
    prevPage,
  });

  const sortData = gridSorting?.[0];
  return useQuery({
    queryKey: [
      "get-patient-payment-v2",
      pageIndex,
      pagination.pageSize,
      gridSorting,
      sortData,
      patientUuid,
      typeOfPayment,
      transactionDate,
      transactionId,
      paymentMethod,
      paymentCategory,
      searchString,
      visitId,
      superbillId,
      claimId,
      status,
      archive,
      creditBalance,
      outstandingBalance,
      enabled,
    ],
    refetchOnWindowFocus: false,

    placeholderData: keepPreviousData,
    enabled: enabled && !!typeOfPayment,
    queryFn: async () =>
      new PatientPaymentControllerApi(getConfigObject())
        .getPatientAllPaymentV2(
          pageIndex, //page
          pagination.pageSize, //size
          sortData?.field ?? undefined, //sortBy
          paymentMethod, //paymentMode
          paymentCategory, //paymentCategory
          typeOfPayment, //typeOfPayment
          sortData?.sort ?? undefined, //sort
          transactionId, //transactionId
          transactionDate, //transactionDate
          patientUuid, //patientUuid
          visitId, //visitId
          superbillId, //superbillId
          claimId, //claimId
          status, //status
          archive, //archive
          searchString, //searchString
          creditBalance, //creditBalance
          outstandingBalance, //outstandingBalance
          getXtenantId() //xTENANTID
        )
        .then((it) => it?.data),
  });
}
